import { el, fragment, clear } from '../dom.js';
import { admin } from '../admin-api.js';
import { t } from '../i18n.js';
import { mountChart, baseOptions, chartTheme } from '../ui/chart.js';
import { statsState } from './stats-state.js';

// The publisher-facing numbers: page views, calendar adds, outbound ticket
// clicks, and the ranked breakdowns behind them. Operational health — mail,
// the occurrence window, backups, job runs — moved to its own page at
// /admin/health, read in an emergency rather than on a weekly pass through
// this one.
const RANGES = [
  { key: 1, label: () => t.statsRange24h },
  { key: 7, label: () => t.statsRange7d },
  { key: 30, label: () => t.statsRange30d },
  { key: 90, label: () => t.statsRange90d },
  { key: 365, label: () => t.statsRange365d },
];

// The router tears a view down with clear(outlet) and nothing else — there is
// no unmount hook that reaches this module, so the last mount has to be torn
// down by the next one. Same shape as `mounted` in calendar.js, and the same
// argument: this page sits open in a tab across a moderator's weekly pass,
// not a single render-and-forget.
let activeMount = null;

// Bumping `generation` does two jobs at once: it destroys every chart this
// mount currently has attached, and it orphans any mountChart() promise still
// in flight from an earlier load() — one whose node a later clear(body) has
// already detached. Read below in trafficSection(): a chart that resolves
// against a stale generation destroys itself on arrival instead of joining
// `charts` and waiting for a teardown that may never come again once the
// moderator navigates away.
function teardown(mount) {
  mount.generation += 1;
  mount.pending.length = 0;
  for (const chart of mount.charts.splice(0)) {
    try { chart.destroy(); } catch { /* gone */ }
  }
  return mount.generation;
}

// ApexCharts measures its container when it renders, so the node has to be in
// the document first — and on the very first render it is not: the router
// appends this view's tree in the microtask that follows renderStats()
// resolving, which is after everything load() could queue from inside itself.
// A macrotask runs only once every pending microtask has drained, so a zero
// timeout lands after that append by definition rather than by winning a race
// against a chunk fetch. On a pill click the body is already in the document
// and this costs a tick nobody can see, which is the price of one rule instead
// of two.
function flush(mount, generation) {
  setTimeout(() => {
    if (generation !== mount.generation) return;
    for (const start of mount.pending.splice(0)) start();
  }, 0);
}

export async function renderStats() {
  if (activeMount) teardown(activeMount);
  let days = 30;
  const body = el('div', {});
  const mount = { generation: 0, charts: [], pending: [] };
  activeMount = mount;

  // Fetched once for this mount's whole life, started here rather than inside
  // load(): /admin/api/inventory takes no `days` argument, so a range pill
  // changes only the Umami window — re-running inventory's ~10 COUNT queries
  // on every click would answer the same numbers five times over.
  const invPromise = admin('GET', '/inventory');

  const pills = el(
    'div',
    { class: 'filter-pills', role: 'group', 'aria-label': t.statsRangeLabel },
    RANGES.map((range) => {
      const button = el('button', {
        class: 'filter-pill', type: 'button', 'aria-pressed': String(range.key === days),
      }, range.label());
      button.addEventListener('click', () => {
        days = range.key;
        for (const other of pills.children) other.setAttribute('aria-pressed', String(other === button));
        load();
      });
      return button;
    })
  );

  async function load() {
    // Destroyed before the repaint, or a range switch leaves the old chart's
    // canvas and its listeners behind on every click.
    const generation = teardown(mount);
    clear(body);
    body.append(el('p', { class: 'pub-empty' }, t.loading));

    // Both at once, and each with its own empty state: Umami may be off or
    // unreachable while the inventory always answers, and the page has to show
    // that rather than collapse to one message.
    //
    // Settled and not all, for the same reason readStats() reads its four
    // aggregates that way: admin() resolves a non-2xx to [], so what reaches
    // here as a rejection is the network itself failing (offline, DNS, a
    // dropped connection) — and a dropped connection on one of the two must
    // not blank the other, or the split into two endpoints buys nothing at the
    // one moment it was meant for.
    const [statsLeg, invLeg] = await Promise.allSettled([
      admin('GET', `/stats?days=${days}`),
      invPromise,
    ]);

    // A newer click already started its own load() while this one was still
    // in flight — the response that just arrived describes a range nobody is
    // looking at anymore.
    if (generation !== mount.generation) return;

    for (const leg of [statsLeg, invLeg]) {
      // Logged rather than shown: the half that failed renders its own state
      // below, and the reason belongs in a console a moderator can be asked
      // to open, not in a dashboard.
      if (leg.status === 'rejected') console.error(leg.reason);
    }

    // The sentinel the two halves already read as "this did not answer" — see
    // statsState(). A rejection produces the same page as a 500, which is the
    // truth in both cases.
    const stats = statsLeg.status === 'fulfilled' ? statsLeg.value : [];
    const inv = invLeg.status === 'fulfilled' ? invLeg.value : [];

    clear(body);
    body.append(analytics(stats, mount, generation), inventory(inv));
    flush(mount, generation);
  }

  await load();
  return fragment([
    el('h1', { class: 'pub-title' }, t.stats),
    el('p', { class: 'pub-lede' }, t.statsLede),
    pills,
    body,
  ]);
}

// ------------------------------------------------------------- analytics

function analytics(stats, mount, generation) {
  const state = statsState(stats);

  // A non-2xx response reads as `[]` — see statsState()'s own comment — and
  // that is a request that failed, not a switch that is off. Conflating the
  // two tells a moderator to edit an env var for what is actually an expired
  // session or a 500 on the Umami read.
  if (state === 'failed') {
    return el('div', { class: 'pub-empty' }, t.statsFailed);
  }

  if (state === 'off') {
    return el('div', { class: 'pub-empty' }, [
      el('p', {}, t.statsOff),
      el('p', { class: 'card-meta' }, t.statsOffHint),
    ]);
  }

  // The numbers arrive with the server and never with the browser: the read
  // token stays on our side of the wire, which is the only reason this route
  // exists instead of the dashboard talking to Umami directly.
  const range = rangeLabel(stats.days);
  const head = el('div', { class: 'card' }, [
    el('h3', { class: 'card-title' }, t.stats),
    el('p', { class: 'card-meta' }, range ? `${stats.host ?? '—'} · ${range}` : (stats.host ?? '—')),
    el('p', { class: 'card-meta' }, t.statsAggregate),
  ]);

  // Two honest empty states rather than one, because they need different
  // answers: no token is something to configure, and no response is
  // something to wait out.
  if (state === 'no-token') {
    head.append(el('p', { class: 'card-meta' }, t.statsNoToken));
    return head;
  }
  if (state === 'unreachable') {
    head.append(el('p', { class: 'card-meta' }, t.statsUnreachable));
    return head;
  }

  const summary = stats.summary;
  return fragment([
    head,
    el('div', { class: 'stats-tiles' }, [
      tile(t.statsTileVisitors, summary.visitors),
      tile(t.statsPageviews, summary.pageviews),
      tile(t.statsTileCalendarAdds, eventCount(summary.events, 'calendar_add')),
      tile(t.statsTileTicketClicks, eventCount(summary.events, 'ticket_click')),
    ]),
    trafficSection(summary, mount, generation),
    funnelSection(summary),
    venuesSection(summary),
  ]);
}

function rangeLabel(days) {
  return RANGES.find((range) => range.key === days)?.label() ?? '';
}

function eventCount(events, name) {
  return events?.find((row) => row.name === name)?.count ?? 0;
}

function trafficSection(summary, mount, generation) {
  const section = el('section', { class: 'mod-section' }, [
    el('h3', { class: 'bucket-title' }, t.statsTraffic),
  ]);

  // `series` is nullable on its own — one Umami endpoint refusing costs only
  // this chart, not the tiles above it — and a present-but-empty series (a
  // fresh site, nothing in range) gets the same honest answer as a missing
  // one rather than an empty rectangle nobody can read as "nothing yet".
  const points = (summary.series?.sessions?.length ?? 0) + (summary.series?.pageviews?.length ?? 0);
  if (!summary.series || points === 0) {
    section.append(el('p', { class: 'pub-empty' }, t.statsNoData));
    return section;
  }

  const node = el('div', { class: 'stats-chart' });
  section.append(node);

  const theme = chartTheme();
  const options = baseOptions(theme, { unit: summary.series.unit });
  // Queued rather than started: mountChart() requires the node to be in the
  // document, and it is not until load() has appended this tree and flush()
  // has run. A mount this pass never reaches — the moderator clicked away
  // first — is dropped by teardown() emptying the queue.
  mount.pending.push(() => mountChart(node, {
    ...options,
    chart: { ...options.chart, type: 'area' },
    colors: [theme.accent, theme.muted],
    series: [
      { name: t.statsVisitors, data: (summary.series.sessions ?? []).map((p) => [new Date(p.x).getTime(), p.y]) },
      { name: t.statsPageviews, data: (summary.series.pageviews ?? []).map((p) => [new Date(p.x).getTime(), p.y]) },
    ],
    // A chart that cannot mount must leave the tiles above it standing —
    // mountChart() returns null rather than throwing for exactly this case.
  }).then((chart) => {
    if (!chart) return;
    // Superseded while the chunk was loading — another pill click, or a
    // return visit to this page that has already torn the mount down. Destroy
    // on arrival rather than join `charts` and wait for a teardown that may
    // never run again.
    if (generation !== mount.generation) {
      try { chart.destroy(); } catch { /* gone */ }
      return;
    }
    mount.charts.push(chart);
  }));

  return section;
}

function funnelSection(summary) {
  const section = el('section', { class: 'mod-section' }, [
    el('h3', { class: 'bucket-title' }, t.statsFunnel),
  ]);
  const events = summary.events ?? [];
  section.append(
    events.length
      ? rankList(events.map((row) => ({ label: row.name, count: row.count })))
      : el('p', { class: 'pub-empty' }, t.statsNoData)
  );
  return section;
}

function venuesSection(summary) {
  const section = el('section', { class: 'mod-section' }, [
    el('h3', { class: 'bucket-title' }, t.statsVenues),
  ]);
  const venues = summary.venues ?? [];
  section.append(
    venues.length
      ? rankList(venues.map((row) => ({ label: row.venue, count: row.count })))
      : el('p', { class: 'pub-empty' }, t.statsNoData)
  );
  return section;
}

// A bar drawn behind the row's own label rather than a second chart type —
// each of these lists is five to twenty numbers, not a plot. The fill is
// --surface-15, never the accent: §2.3 forbids the accent behind text, and a
// ranked list is exactly that. Width is a custom property set from JS per
// row, the same pattern the side panel already uses to place itself against
// the calendar's own measured box (see openPanel in dom.js).
function rankList(rows) {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return el('ul', { class: 'card-list stats-rank' }, rows.map((row) => {
    const item = el('li', { class: 'stats-rank-row' }, [
      el('span', { class: 'stats-rank-label' }, row.label),
      el('span', { class: 'stats-rank-count' }, String(row.count)),
    ]);
    item.style.setProperty('--pct', `${Math.round((row.count / max) * 100)}%`);
    return item;
  }));
}

function tile(label, value) {
  return el('div', { class: 'stats-tile' }, [
    el('div', { class: 'stats-tile-label' }, label),
    el('div', { class: 'stats-tile-value' }, String(value ?? 0)),
  ]);
}

// -------------------------------------------------------------- inventory

// Reads only from /admin/api/inventory, and never checks whether analytics is
// on — the SQL behind it needs no token and always answers. The request
// itself can still fail on the way there (an expired session, a dropped
// connection), and that comes back from admin() as the array sentinel rather
// than the object this page expects — checked first, or a failed request
// renders as a database with nothing in it.
function inventory(inv) {
  if (Array.isArray(inv)) {
    return el('div', { class: 'pub-empty' }, t.statsInventoryFailed);
  }

  return fragment([
    el('section', { class: 'mod-section' }, [
      el('h3', { class: 'bucket-title' }, t.statsInventory),
      el('div', { class: 'stats-tiles' }, [
        tile(t.statsPublishing, inv?.venues?.publishing),
        tile(t.statsIdle, inv?.venues?.idle),
        tile(t.statsPublished7d, inv?.published7d),
        tile(t.statsNext7, inv?.dates?.next7),
        tile(t.statsNext30, inv?.dates?.next30),
      ]),
      byCitySection(inv?.dates?.byCity ?? []),
      incompleteSection(inv?.incomplete),
    ]),
    canary(inv?.codes),
  ]);
}

function byCitySection(byCity) {
  return fragment([
    el('h4', { class: 'ev-section' }, t.statsByCity),
    byCity.length
      ? rankList(byCity.map((row) => ({ label: row.city, count: row.count })))
      : el('p', { class: 'pub-empty' }, t.statsNoData),
  ]);
}

function incompleteSection(incomplete) {
  return fragment([
    el('h4', { class: 'ev-section' }, t.statsIncomplete),
    el('div', { class: 'stats-tiles' }, [
      tile(t.statsNoImage, incomplete?.noImage),
      tile(t.statsNoTag, incomplete?.noTag),
      tile(t.statsNoAddress, incomplete?.noPlace),
    ]),
  ]);
}

// The canary: issued against consumed. A gap between the two is mail not
// arriving, and the tile has to say so — two bare numbers are a puzzle rather
// than a signal.
function canary(codes) {
  return el('div', { class: 'card' }, [
    el('h3', { class: 'card-title' }, t.statsCanary),
    el('p', {}, t.statsCanaryCount(codes?.consumed ?? 0, codes?.issued ?? 0)),
    el('p', { class: 'card-meta' }, t.statsCanaryHint),
  ]);
}
